import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, ArrowLeft, Upload, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const GetQuote = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1
    projectType: '',
    // Step 2
    features: [],
    // Step 3
    budget: '',
    timeline: '',
    // Step 4
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const projectTypes = [
    { id: 'mobile', name: 'Mobil Uygulama', icon: '📱', desc: 'iOS & Android' },
    { id: 'web', name: 'Web Sitesi', icon: '🌐', desc: 'Responsive web' },
    { id: 'ecommerce', name: 'E-Ticaret', icon: '🛒', desc: 'Online mağaza' },
    { id: 'ai', name: 'AI Entegrasyonu', icon: '🤖', desc: 'Yapay zeka' },
    { id: 'seo', name: 'SEO & Dijital', icon: '📈', desc: 'Organik büyüme' },
    { id: 'social', name: 'Sosyal Medya', icon: '📣', desc: 'Marka yönetimi' }
  ];

  const features = [
    'Kullanıcı Girişi & Kayıt',
    'Ödeme Sistemi',
    'Admin Paneli',
    'Push Notifications',
    'Sosyal Medya Entegrasyonu',
    'Çoklu Dil Desteği',
    'Analytics & Raporlama',
    'API Entegrasyonu',
    'Canlı Chat',
    'E-posta Bildirimleri',
    'Arama & Filtreleme',
    'Harita Entegrasyonu'
  ];

  const budgetRanges = [
    { id: '10-25', label: '₺10.000 - ₺25.000', value: '10-25' },
    { id: '25-50', label: '₺25.000 - ₺50.000', value: '25-50' },
    { id: '50-100', label: '₺50.000 - ₺100.000', value: '50-100' },
    { id: '100+', label: '₺100.000+', value: '100+' }
  ];

  const timelines = [
    { id: '1-2', label: '1-2 Ay', value: '1-2' },
    { id: '2-3', label: '2-3 Ay', value: '2-3' },
    { id: '3-6', label: '3-6 Ay', value: '3-6' },
    { id: '6+', label: '6+ Ay', value: '6+' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Quote form submitted:', formData);
    
    toast({
      title: "Teklif Talebiniz Alındı! 🎉",
      description: "Ekibimiz 24-48 saat içinde size özel bir teklif hazırlayacak.",
    });

    // Redirect to thank you page
    setTimeout(() => {
      navigate('/tesekkurler?type=quote');
    }, 1500);
  };

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const progress = (currentStep / 4) * 100;

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      <section className="relative pt-32 pb-20 min-h-screen">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-cyan-400 animate-pulse" />
              <span className="text-cyan-400 text-sm font-medium">Ücretsiz Teklif</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Projeniz İçin
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Özel Teklif</span>
            </h1>
            <p className="text-gray-400 text-lg">
              4 adımda projenizi tanımlayın, size özel teklif hazırlayalım
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between mb-2">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
                    step <= currentStep
                      ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white'
                      : 'bg-slate-800 text-gray-500'
                  }`}
                >
                  {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
                </div>
              ))}
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 min-h-[500px]">
              {/* Step 1: Project Type */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Proje Türünüzü Seçin</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {projectTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, projectType: type.id })}
                        className={`p-6 rounded-xl border-2 transition-all text-left ${
                          formData.projectType === type.id
                            ? 'border-cyan-500 bg-cyan-500/10'
                            : 'border-slate-700 hover:border-cyan-500/50'
                        }`}
                      >
                        <div className="text-4xl mb-3">{type.icon}</div>
                        <h3 className="text-lg font-bold text-white mb-1">{type.name}</h3>
                        <p className="text-gray-400 text-sm">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Features */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-white mb-6">İhtiyacınız Olan Özellikler</h2>
                  <p className="text-gray-400 mb-4">Birden fazla seçebilirsiniz</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {features.map((feature) => (
                      <button
                        key={feature}
                        type="button"
                        onClick={() => toggleFeature(feature)}
                        className={`p-4 rounded-xl border transition-all text-left flex items-center gap-3 ${
                          formData.features.includes(feature)
                            ? 'border-cyan-500 bg-cyan-500/10'
                            : 'border-slate-700 hover:border-cyan-500/50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          formData.features.includes(feature)
                            ? 'border-cyan-500 bg-cyan-500'
                            : 'border-slate-600'
                        }`}>
                          {formData.features.includes(feature) && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className="text-white">{feature}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Budget & Timeline */}
              {currentStep === 3 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-6">Bütçe ve Zaman Çizelgesi</h2>
                    
                    <Label className="text-white mb-3 block">Bütçe Aralığınız</Label>
                    <div className="grid md:grid-cols-2 gap-4 mb-8">
                      {budgetRanges.map((range) => (
                        <button
                          key={range.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, budget: range.value })}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.budget === range.value
                              ? 'border-cyan-500 bg-cyan-500/10'
                              : 'border-slate-700 hover:border-cyan-500/50'
                          }`}
                        >
                          <span className="text-white font-semibold">{range.label}</span>
                        </button>
                      ))}
                    </div>

                    <Label className="text-white mb-3 block">Hedef Teslim Süresi</Label>
                    <div className="grid md:grid-cols-2 gap-4">
                      {timelines.map((time) => (
                        <button
                          key={time.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, timeline: time.value })}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.timeline === time.value
                              ? 'border-cyan-500 bg-cyan-500/10'
                              : 'border-slate-700 hover:border-cyan-500/50'
                          }`}
                        >
                          <span className="text-white font-semibold">{time.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Contact Info */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-white mb-6">İletişim Bilgileriniz</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-white mb-2 block">Ad Soyad *</Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                        placeholder="Ahmet Yılmaz"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-white mb-2 block">E-posta *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                        placeholder="ahmet@sirket.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-white mb-2 block">Telefon</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                        placeholder="+90 XXX XXX XX XX"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-white mb-2 block">Şirket</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                        placeholder="Şirket Adı"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white mb-2 block">Ek Notlar</Label>
                    <Textarea
                      id="message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="bg-slate-900/50 border-slate-700 text-white resize-none"
                      placeholder="Projeniz hakkında eklemek istedikleriniz..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <Button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                variant="outline"
                className="border-slate-700 text-white disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Geri
              </Button>

              {currentStep < 4 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  disabled={
                    (currentStep === 1 && !formData.projectType) ||
                    (currentStep === 3 && (!formData.budget || !formData.timeline))
                  }
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                >
                  İleri
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                >
                  Teklif Al
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GetQuote;
